import { Status } from '../components/create-defect/types'
import { getColor, MaybeColor } from '../Theme'
import { Criticality, Fault, Finding, TaskEquipment } from '../types'

export const ALWAYS_RESTRICTED_LEVELS = ['1', '2', '4']

export const isServiceLimitation = (item: { restrictionCode?: string | null }): boolean =>
  item.restrictionCode?.startsWith('P') ?? false

type FindMostCriticalOptions = {
  ignoreServiceLimitations?: boolean
}

const getApparentCriticalityLevel = (
  fault: Fault,
  ignoreOptions: FindMostCriticalOptions
): Criticality => {
  if (ignoreOptions.ignoreServiceLimitations && isServiceLimitation(fault)) {
    return { level: '3', description: 'Ei rajoita käyttöä' }
  } else if (fault.usageCode && fault.criticality.level === '3') {
    return {
      level: '4', // if prio3 with usagecode handle as prio4
      description: 'Rajoittaa käyttöä',
    }
  }
  return fault.criticality
}

export const findMostCritical = (
  findings: Array<Finding>,
  ignoreOptions: FindMostCriticalOptions = {}
) => {
  let criticality: Criticality | null = null
  for (const finding of findings) {
    for (const fault of finding.faults) {
      const apparentCriticality = getApparentCriticalityLevel(fault, ignoreOptions)

      if (
        criticality === null ||
        compareCriticality('' + apparentCriticality.level, '' + criticality.level)
      ) {
        criticality = {
          ...apparentCriticality,
          ...{ equipmentId: finding.equipment.equipmentId },
        }
      }
    }
  }
  return criticality
}

export const findMostCriticalFaultLevel = (finding: Finding): string => {
  const faults = finding.faults
  let criticality: Criticality | null = null
  if (faults.length > 0) {
    faults.forEach((fault) => {
      const apparentCriticality = getApparentCriticalityLevel(fault, {
        ignoreServiceLimitations: false,
      })

      if (
        criticality === null ||
        compareCriticality('' + apparentCriticality.level, '' + criticality.level)
      ) {
        criticality = { ...apparentCriticality, ...{ equipmentId: finding.equipment.equipmentId } }
      }
    })
  }
  return criticality === null ? '' : (criticality as Criticality).level
}

export const compareFaults = (a: Fault, b: Fault) => {
  //Criticality levels are from highest to lowest 1,2,4,3
  const aLevel =
    a.criticality.level === '3' ? parseInt(a.criticality.level) + 2 : parseInt(a.criticality.level)
  const bLevel =
    b.criticality.level === '3' ? parseInt(b.criticality.level) + 2 : parseInt(b.criticality.level)
  return aLevel - bLevel
}

//The criticality level priority is not linear. 1 is most critical but 3 is less critical than 4.
//returns true if the first parameter is of higher criticality than the second one
const compareCriticality = (criticalityLevel1: string | null, criticalityLevel2: string | null) => {
  const criticalities = '1243'
  if (!criticalityLevel1) {
    return false
  } else if (!criticalityLevel2) {
    return true
  }
  return criticalities.indexOf(criticalityLevel1) < criticalities.indexOf(criticalityLevel2)
}

export const chooseTextColor = (criticality: string | null, nightMode: boolean) => {
  if (!criticality) {
    return getColor({ nightMode }, ['white'], ['black'])
  }
  if (criticality === '1') {
    return getColor({ nightMode }, ['red'], ['red'])
  }
  if (criticality === '2') {
    return getColor({ nightMode }, ['red'], ['red'])
  }
  if (criticality === '3') {
    return getColor({ nightMode }, ['secondaryGreen'], ['secondaryGreen'])
  }
  if (criticality === '4') {
    return getColor({ nightMode }, ['primaryYellow'], ['primaryYellow'])
  }
}

/** fill and stroke colors are reversed in night mode */
const getStrokeAndFillColors = (
  fillColorDay: MaybeColor,
  strokeColorDay: MaybeColor,
  nightMode: boolean,
  fillColorSecondary?: MaybeColor,
  strokeColorSecondary?: MaybeColor
) => {
  return {
    fillColor: getColor({ nightMode }, [fillColorDay], [strokeColorDay]),
    fillColorSecondary: fillColorSecondary
      ? getColor({ nightMode }, [fillColorSecondary], [fillColorSecondary])
      : undefined,
    strokeColor: getColor({ nightMode }, [strokeColorDay], [fillColorDay]),
    strokeColorSecondary: strokeColorSecondary
      ? getColor({ nightMode }, [strokeColorSecondary], [strokeColorSecondary])
      : undefined,
  }
}

export const chooseIconColor = (
  criticality: string | null,
  isRestrictive: boolean,
  status: Status,
  nightMode: boolean
) => {
  if (status === 'closed') {
    return getStrokeAndFillColors('grayLight', 'nightBlack', false)
  }

  if (criticality === '1') {
    return getStrokeAndFillColors('red', 'black', nightMode)
  }
  if (criticality === '2') {
    return getStrokeAndFillColors('primaryYellow', 'black', nightMode)
  }
  if (criticality === '3') {
    return getStrokeAndFillColors(
      'secondaryGreen',
      'black',
      nightMode,
      isRestrictive ? 'primaryYellow' : undefined,
      isRestrictive ? 'black' : undefined
    )
  }
  if (criticality === '4') {
    return getStrokeAndFillColors('secondaryGreen', 'black', nightMode)
  }

  return getStrokeAndFillColors('white', 'black', nightMode)
}

export const chooseButtonColor = (
  criticality: string | null,
  nightMode: boolean,
  loading: boolean | null
) => {
  if (loading) return getColor({ nightMode }, ['transparent'], ['transparent'])
  if (criticality === null) {
    return getColor({ nightMode }, ['grayDark'], ['grayDark'])
  }
  if (criticality === '1') {
    return getColor({ nightMode }, ['red'], ['red'])
  }
  if (criticality === '2') {
    return getColor({ nightMode }, ['primaryYellow'], ['primaryYellow'])
  }
  if (criticality === '3') {
    return getColor({ nightMode }, ['primaryBlue'], ['primaryBlue'])
  }
  if (criticality === '4') {
    return getColor({ nightMode }, ['primaryYellow'], ['primaryYellow'])
  }
}

/** The function assumes level 3s do not have usageCodes */
export const taskHasARestriction = (equipment: TaskEquipment | null | undefined): boolean =>
  (equipment?.highestCriticality?.level ?? '3') !== '3'
